// 角色常量
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user'
}

// 权限常量
export const PERMISSIONS = {
  // 用户管理
  VIEW_USERS: 'view_users',
  CREATE_USER: 'create_user',
  UPDATE_USER: 'update_user',
  DELETE_USER: 'delete_user',
  MANAGE_USER_STATUS: 'manage_user_status',

  // Token管理
  VIEW_TOKENS: 'view_tokens',
  CREATE_TOKEN: 'create_token',
  UPDATE_TOKEN: 'update_token',
  DELETE_TOKEN: 'delete_token',
  MANAGE_TOKEN_STATUS: 'manage_token_status',

  // 交易数据
  VIEW_TRADE_DATA: 'view_trade_data',
  UPLOAD_TRADE_DATA: 'upload_trade_data',
  EXPORT_TRADE_DATA: 'export_trade_data',

  // 报告
  VIEW_REPORTS: 'view_reports',
  GENERATE_REPORTS: 'generate_reports',
  DOWNLOAD_REPORTS: 'download_reports',

  // 在线用户管理
  VIEW_ONLINE_USERS: 'view_online_users',
  FORCE_LOGOUT: 'force_logout',

  // 仪表盘
  VIEW_DASHBOARD: 'view_dashboard',

  // 综合全景（管理员专用，聚合资金走势与交易趋势）
  VIEW_COMPOSITE_OVERVIEW: 'view_composite_overview',

  // 资金流水
  VIEW_TRANSACTIONS: 'view_transactions',

  // 账号配置
  VIEW_ACCOUNT_CONFIG: 'view_account_config',

  // 资金走势
  VIEW_FUND_TRENDS: 'view_fund_trends',

  // 对冲组监控
  VIEW_HEDGE_GROUPS: 'view_hedge_groups',

  // 告警管理
  VIEW_ALERTS: 'view_alerts',
  MANAGE_ALERTS: 'manage_alerts'
}

// 角色权限映射
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    // 管理员拥有所有权限
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.CREATE_USER,
    PERMISSIONS.UPDATE_USER,
    PERMISSIONS.DELETE_USER,
    PERMISSIONS.MANAGE_USER_STATUS,
    PERMISSIONS.VIEW_TOKENS,
    PERMISSIONS.CREATE_TOKEN,
    PERMISSIONS.UPDATE_TOKEN,
    PERMISSIONS.DELETE_TOKEN,
    PERMISSIONS.MANAGE_TOKEN_STATUS,
    PERMISSIONS.VIEW_TRADE_DATA,
    PERMISSIONS.UPLOAD_TRADE_DATA,
    PERMISSIONS.EXPORT_TRADE_DATA,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.GENERATE_REPORTS,
    PERMISSIONS.DOWNLOAD_REPORTS,
    PERMISSIONS.VIEW_ONLINE_USERS,
    PERMISSIONS.FORCE_LOGOUT,
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_COMPOSITE_OVERVIEW,
    PERMISSIONS.VIEW_TRANSACTIONS,
    PERMISSIONS.VIEW_ACCOUNT_CONFIG,
    PERMISSIONS.VIEW_FUND_TRENDS,
    PERMISSIONS.VIEW_HEDGE_GROUPS,
    PERMISSIONS.VIEW_ALERTS,
    PERMISSIONS.MANAGE_ALERTS
  ],
  [ROLES.USER]: [
    // 普通用户的权限
    PERMISSIONS.VIEW_TRADE_DATA,
    PERMISSIONS.UPLOAD_TRADE_DATA,
    PERMISSIONS.EXPORT_TRADE_DATA,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.GENERATE_REPORTS,
    PERMISSIONS.DOWNLOAD_REPORTS,
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ACCOUNT_CONFIG,
    // Token管理
    PERMISSIONS.VIEW_TOKENS,
    PERMISSIONS.CREATE_TOKEN,
    PERMISSIONS.UPDATE_TOKEN,
    PERMISSIONS.DELETE_TOKEN,
    PERMISSIONS.MANAGE_TOKEN_STATUS,
    // 统计与全景（后端按账号归属过滤，仅见本人数据）
    PERMISSIONS.VIEW_COMPOSITE_OVERVIEW,
    PERMISSIONS.VIEW_TRANSACTIONS,
    PERMISSIONS.VIEW_FUND_TRENDS,
    PERMISSIONS.VIEW_HEDGE_GROUPS,
    PERMISSIONS.VIEW_ALERTS
  ]
}

// 页面路由权限映射
export const ROUTE_PERMISSIONS = {
  '/dashboard': [PERMISSIONS.VIEW_DASHBOARD],
  '/composite-overview': [PERMISSIONS.VIEW_COMPOSITE_OVERVIEW],
  '/users': [PERMISSIONS.VIEW_USERS],
  '/tokens': [PERMISSIONS.VIEW_TOKENS],
  '/trade-data': [PERMISSIONS.VIEW_TRADE_DATA],
  '/reports': [PERMISSIONS.VIEW_REPORTS],
  '/online-users': [PERMISSIONS.VIEW_ONLINE_USERS],
  '/transactions': [PERMISSIONS.VIEW_TRANSACTIONS],
  '/account-configs': [PERMISSIONS.VIEW_ACCOUNT_CONFIG],
  '/business-lines': [PERMISSIONS.VIEW_ACCOUNT_CONFIG],
  '/strategy-types': [PERMISSIONS.VIEW_ACCOUNT_CONFIG],
  '/fund-trends': [PERMISSIONS.VIEW_FUND_TRENDS],
  '/hedge-monitor': [PERMISSIONS.VIEW_HEDGE_GROUPS],
  '/raven-download': [PERMISSIONS.VIEW_TRADE_DATA],
  '/alerts': [PERMISSIONS.VIEW_ALERTS],
  '/alert-rules': [PERMISSIONS.MANAGE_ALERTS]
}

/**
 * 检查用户是否有指定权限
 * @param {Object} user - 用户对象
 * @param {string} permission - 权限名称
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  // Testing override: if using fake auth token, grant all permissions
  const token = localStorage.getItem('authToken');
  if (token === 'fake_jwt_token_for_testing') {
    return true;
  }

  if (!user || !user.role) {
    return false
  }

  const userPermissions = ROLE_PERMISSIONS[user.role] || []
  return userPermissions.includes(permission)
}

/**
 * 检查用户是否有访问指定路由的权限
 * @param {Object} user - 用户对象
 * @param {string} route - 路由路径
 * @returns {boolean}
 */
export const hasRoutePermission = (user, route) => {
  if (!user) {
    return false
  }

  const requiredPermissions = ROUTE_PERMISSIONS[route]
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true // 如果路由没有权限要求，则允许访问
  }

  return requiredPermissions.some(permission => hasPermission(user, permission))
}

/**
 * 检查用户是否有指定角色
 * @param {Object} user - 用户对象
 * @param {string} role - 角色名称
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  if (!user || !user.role) {
    return false
  }

  return user.role === role
}

/**
 * 检查用户是否是管理员
 * @param {Object} user - 用户对象
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return hasRole(user, ROLES.ADMIN)
}

/**
 * 检查用户是否有多个权限中的任意一个
 * @param {Object} user - 用户对象
 * @param {Array} permissions - 权限数组
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!Array.isArray(permissions)) {
    return false
  }

  return permissions.some(permission => hasPermission(user, permission))
}

/**
 * 检查用户是否有多个权限中的所有权限
 * @param {Object} user - 用户对象
 * @param {Array} permissions - 权限数组
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!Array.isArray(permissions)) {
    return false
  }

  return permissions.every(permission => hasPermission(user, permission))
}

/**
 * 获取用户所有权限
 * @param {Object} user - 用户对象
 * @returns {Array}
 */
export const getUserPermissions = (user) => {
  if (!user || !user.role) {
    return []
  }

  return ROLE_PERMISSIONS[user.role] || []
} 